
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { cloneDeep, escapeRegExp } from 'lodash';
import { ValidationObserver } from 'vee-validate';
import { Validate } from '@/types';
import { toErrorMessage } from '@/utils/errorHandling';
import Toast from '@/utils/toast';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import PaymentMethodValidationForm from '@/components/advanced/PaymentMethodValidationForm.vue';

const namespace: string = 'auth';

@Component<ExtraPaymentsValidationModal>({
	components: {
		StandardModal,
		PaymentMethodValidationForm,
		ValidationObserver
	}
})
export default class ExtraPaymentsValidationModal extends Vue {
	@Action('updateRestaurant', { namespace }) private updateRestaurant!: (payload: { restaurant: object }) => Promise<void>;
	@Getter('getExtraPaymentMethods', { namespace }) private extraPaymentMethods!: RestaurantExtraPaymentMethod[] | null;
	@Getter('getRestaurant', { namespace }) private restaurant!: Restaurant;
	@Prop({ type: Boolean, required: false, default: false }) private showModal!: boolean;

	$refs!: { observer: Validate; };

	private mutableExtraPaymentMethods: RestaurantExtraPaymentMethod[] = [];
	private activeTab: number = 0;
	private loading: boolean = false;

	private get costCenterMethod(): RestaurantExtraPaymentMethod | undefined {
		return this.mutableExtraPaymentMethods.find(method => method.type === 'cost_center');
	}

	private get purchaseOrderMethod(): RestaurantExtraPaymentMethod | undefined {
		return this.mutableExtraPaymentMethods.find(method => method.type === 'purchase_order');
	}

	private get costCenterLabel(): string {
		return this.costCenterMethod ? this.resolvePaymentMethodLabel(this.costCenterMethod) : '';
	}

	private get purchaseOrderLabel(): string {
		return this.purchaseOrderMethod ? this.resolvePaymentMethodLabel(this.purchaseOrderMethod) : '';
	}

	private created(): void {
		this.mutableExtraPaymentMethods = cloneDeep(this.extraPaymentMethods ?? []);
	}

	/**
	 * Resolve a payment method's display label: its localized custom label if set,
	 * otherwise the default translation for its type (e.g. "Cost center" / "Purchase order").
	 *
	 * @param {RestaurantExtraPaymentMethod} method
	 * @return {string}
	 */
	private resolvePaymentMethodLabel(method: RestaurantExtraPaymentMethod): string {
		return Vue.filter('localize')(method, 'label') || this.$t(`orders.management.order_modal.payment_preferences.${method.type}`);
	}

	/**
	 * Validate and save the configured rules across both tabs
	 *
	 * @return {Promise<void>}
	 */
	private async save(): Promise<void> {
		const isValid: boolean = await this.$refs.observer.validate();
		if (!isValid) {
			return;
		}

		const paymentMethods: RestaurantExtraPaymentMethod[] = cloneDeep(this.mutableExtraPaymentMethods);

		// Only builder mode needs compiling — regex mode already holds the user's raw regex.
		paymentMethods.forEach(method => {
			const validation = method.validation;
			if (!validation || validation.mode !== 'builder') {
				return;
			}
			// A builder rule with no prefix, no character length and case-sensitive matching neither
			// constrains the input nor normalizes its casing on save — drop it instead of storing a no-op rule.
			if (!validation.prefix && validation.characterLength == null && validation.caseSensitive) {
				delete method.validation;
				return;
			}
			const { regex, flags } = this.compileBuilderValidation(validation);
			validation.regex = regex;
			validation.flags = flags;
		});

		this.loading = true;
		try {
			await this.updateRestaurant({
				restaurant: {
					id: this.restaurant.id,
					extra_payment_methods: paymentMethods
				}
			});
			Toast.success(this.$t('settings.payment_validation_rules.success'));
			this.$emit('close');
		}
		catch (error) {
			Toast.error(this.$t('settings.payment_validation_rules.error_saving', { errorMessage: toErrorMessage(error) }));
		}
		finally {
			this.loading = false;
		}
	}

	/**
	 * Compile a builder mode validation config into the regex and flags to validate guest input at checkout.
	 *
	 * @param {ExtraPaymentMethodValidation} validation
	 * @return {{ regex: string; flags: '' | 'i' }}
	 */
	private compileBuilderValidation(validation: ExtraPaymentMethodValidation): { regex: string; flags: '' | 'i' } {
		const pattern: string = [
			'^',
			validation.prefix ? `(?=${escapeRegExp(validation.prefix)})` : '',
			validation.characterLength != null ? `.{${validation.characterLength}}` : '.*',
			'$'
		].join('');

		return { regex: pattern, flags: validation.caseSensitive ? '' : 'i' };
	}
}
